import {
	AppButton,
	Checkbox,
	CountBadge,
	TwoPaneMenu,
	TwoPaneMenuCategory
} from '@clickguard/design-system'
import ChevronDown from '@untitled-ui/icons-react/build/cjs/ChevronDown'
import { MouseEvent, useState } from 'react'
import { CONTINENTS, countryNameByCode } from './countries'
import {
	isRegionEmpty,
	NO_REGION,
	RegionSelection,
	WORLDWIDE
} from './region-selection'
import './region-picker.css'

/**
 * The DS's own default. It started at 560 (too wide), went to 360 to match the
 * trigger, and 360 read as cramped (Esteban, 08-04) - so it sits where the DS
 * puts every other TwoPaneMenu.
 *
 * `categoriesWidth` is deliberately NOT set with it. The DS splits the card two
 * fifths / three fifths, which at 460 is 184 and 276, and both of those are the
 * number this menu wants anyway: "Central America" is the longest continent at
 * 104px of text, so it clears its count badge by 35px, and the longest of the
 * 196 country names wants 160 in a 276 column. Nothing truncates in either
 * pane. An explicit 164 held the continents at the width that merely FIT them,
 * which read as tight (Esteban, 08-04) - the split is what looks right, so let
 * the DS keep it.
 */
const PANEL_WIDTH = 460
/**
 * The same card inside the ad-account drawer. The sheet is 460 wide, its body
 * carries 8px of padding and every `SheetSection` another 16, so the widest a
 * panel can be and still clear the card edge is 412.
 */
const COMPACT_PANEL_WIDTH = 412
/** What the DS draws a category row at, inside a 4px column inset. */
const CATEGORY_ROW = 36
const CATEGORIES_INSET = 4
/**
 * Locked pane height so walking the continents never resizes the card, sized to
 * exactly what the continents column wants rather than to a round number. A flat
 * 300 left 4px of dead space under the last row, so the column read as 8px of
 * bottom padding against 4px at the top — enough to see, not enough to name.
 * Derived, so adding a continent keeps it flush.
 */
const PANE_HEIGHT =
	(CONTINENTS.length + 1) * CATEGORY_ROW + CATEGORIES_INSET * 2
/**
 * The DS pins its popover trigger in an `inline-flex w-fit` span it does not
 * expose, so an `AppButton` inside it shrinks to its text no matter what width
 * the button carries. Reach through from the wrapper instead: a descendant
 * selector outranks the DS's own single-class `cg-w-fit`.
 */
const TRIGGER_FIT =
	'w-full [&>span]:w-full [&>span>div]:w-full [&_button]:w-full [&_button]:justify-between'
/**
 * The continent count badge, down from the DS's 20px square to the 16px of the
 * country rows' checkboxes (Esteban, 08-05). The two sit one column apart and
 * read as a pair, so the badge being the larger of them made it the loudest
 * thing in a pane whose job is the continent names. Single digits stay square;
 * two digits still grow sideways off the DS's own padding, which is untouched.
 *
 * `min-w-[16px]` and not `min-w-4`: this app is on Tailwind 3.3.2, where the
 * spacing scale for `min-w` does not exist yet - it arrives in 3.4, and until
 * then `min-w-4` compiles to nothing, silently. The `!` is because the DS's
 * `twMerge` is set up for the `cg-` prefix and cannot see that these conflict
 * with `cg-h-5` / `cg-min-w-5`, so it keeps both and the DS stylesheet, loaded
 * last, wins the tie.
 */
const COUNT_BADGE_SIZE = '!h-4 !min-w-[16px]'
/** How many entries to spell out on the trigger before switching to a count. */
const MAX_INLINE = 2
/**
 * The Worldwide row's category id. It is a category only so it can sit in the
 * left list; it carries no pane and is never made the active category.
 */
const WORLDWIDE_ID = 'worldwide'
/**
 * The "All of <continent>" row. Not a DS feature: it is an ordinary item whose
 * id is handled in `onSelectionChange`, and it is added to the DS's selection
 * only for display, never stored.
 */
const ALL_ID = '__all__'

export type { RegionSelection }
export { WORLDWIDE, NO_REGION, isRegionEmpty }

/**
 * What Worldwide means once you open a continent: every country ticked. Stored
 * state stays the empty `WORLDWIDE` — this is only what the panes are drawn
 * from, and what an edit starts from when you untick your first country.
 */
const EVERY_COUNTRY: Record<string, string[]> = {}
CONTINENTS.forEach((continent) => {
	EVERY_COUNTRY[continent.id] = continent.countries.map((country) => country.id)
})

const TOTAL_COUNTRIES = CONTINENTS.reduce(
	(sum, continent) => sum + continent.countries.length,
	0
)

const isFull = (continentId: string, ids: string[]) => {
	const continent = CONTINENTS.find((entry) => entry.id === continentId)
	return Boolean(continent && ids.length === continent.countries.length)
}

/**
 * Trigger text. Fully picked continents collapse to "Europe (all)" rather than
 * spelling out 44 names, which is the whole point of picking by continent.
 */
const summarize = (value: RegionSelection) => {
	if (value.worldwide) return 'Worldwide'

	const parts: string[] = []
	let total = 0
	CONTINENTS.forEach((continent) => {
		const ids = value.countries[continent.id] || []
		if (!ids.length) return
		total += ids.length
		if (isFull(continent.id, ids)) {
			parts.push(`${continent.label} (all)`)
			return
		}
		ids.forEach((id) => parts.push(countryNameByCode.get(id) || id))
	})

	// Says the state, not the instruction: the red line under the field is where
	// "pick something" belongs, and a trigger reading "Select a region" would say
	// it a second time in the one place that should report what is set.
	if (!parts.length) return 'No region selected'
	// Spell the picks out while they still read as a list; past that they would
	// either wrap the trigger or truncate mid-word, so give the count.
	if (parts.length <= MAX_INLINE) return parts.join(', ')
	return `${total} countries`
}

interface RegionPickerProps {
	value: RegionSelection
	onChange: (next: RegionSelection) => void
	ariaLabel: string
	/**
	 * Greys the trigger and keeps the panel shut. Both are needed: the disabled
	 * button already cannot be clicked, but the popover's open state lives here,
	 * so a panel that was already open has to be forced closed too.
	 */
	disabled?: boolean
	/** Flags the trigger for a screen reader. The visible error is the caller's. */
	invalid?: boolean
	/** Id of the caller's error line, so the trigger points at it. */
	errorId?: string
	/**
	 * For the ad-account drawer, which is a 460px panel at `z-[9999]`.
	 *
	 * Two things change. The panel narrows to fit the drawer, and it anchors to
	 * the scroll container instead of the viewport. The default `fixed` strategy
	 * portals the panel into `document.body` wearing the DS `cg-z-50`, so inside
	 * the drawer it opens BEHIND the sheet and the field reads as dead.
	 */
	compact?: boolean
}

/**
 * Native `title` on whatever label the pointer is over, but only when that
 * label is actually clipped - Esteban's call (08-04) once the panel narrowed to
 * 360: accept the truncation, show the full name on hover when there is more to
 * see. Silent on the rows that already fit, so a tooltip means "there is more".
 *
 * Delegated from the panel rather than wired per row for two reasons. The
 * country rows' labels have to stay plain strings or the DS stops fuzzy-matching
 * and highlighting them (a ReactNode label demands `searchText` and renders
 * verbatim), and the panel portals to `document.body`, out of this subtree -
 * but `TwoPaneMenuProps` extends the div's own HTML attributes, so an
 * `onMouseOver` handed to the menu lands on the portaled panel.
 *
 * It reads the DOM instead of a class name: the row's label is whatever element
 * under the pointer is ellipsizing and overflowing. That survives a DS class
 * rename, and it covers both panes and the search results with one handler.
 *
 * A DS-styled tooltip would have to come from `TwoPaneMenu` itself - there is
 * no truncation helper in the DS today, and hand-rolling a floating layer here
 * is exactly what we ask before doing.
 */
const titleWhenClipped = (event: MouseEvent<HTMLDivElement>) => {
	let node = event.target as HTMLElement | null
	// Up to the row, no further: search highlighting wraps matched characters in
	// their own spans, so the pointer is often two levels under the label.
	for (let depth = 0; node && depth < 4; depth++) {
		if (window.getComputedStyle(node).textOverflow === 'ellipsis') {
			// Cleared, not only set: rows are reused as the panes change and as a
			// search narrows them, so a label that fits again has to lose the
			// tooltip it was given while it did not.
			node.title =
				node.scrollWidth > node.clientWidth ? node.textContent || '' : ''
			return
		}
		node = node.parentElement
	}
}

/**
 * Advertising region picker: continents in the left pane, that continent's
 * countries as checkbox rows in the right, one search field that matches across
 * every continent at once, and Worldwide as a checkbox at the head of the list.
 *
 * This is the DS `TwoPaneMenu` in `selectionMode="multiple"` (0.30.0), not a
 * hand-built composition — the mode already owns the checkbox rows, the
 * per-continent count badges and keeping the popover open across ticks. This
 * file adds the trigger, the summary, the "All of <continent>" row and the
 * Worldwide row.
 *
 * Worldwide and countries are exclusive: ticking Worldwide clears every country
 * and ticking all 196 IS Worldwide, so the checkbox and the panes can never
 * disagree. Unticking Worldwide with nothing else picked leaves no region at
 * all — a real, and invalid, state (see `NO_REGION`).
 */
export const RegionPicker = ({
	value,
	onChange,
	ariaLabel,
	disabled,
	invalid,
	errorId,
	compact = false
}: RegionPickerProps) => {
	const [open, setOpen] = useState(false)
	// Controlled, and never Worldwide: that row is a checkbox, not a drill-in, so
	// clicking it must tick the box and leave the pane on whatever continent you
	// were reading. Starts on the first continent rather than on Worldwide for
	// the same reason - there is no Worldwide pane to land on.
	const [category, setCategory] = useState(CONTINENTS[0].id)

	// Everything below reads this, not `value.countries`: Worldwide is drawn as
	// all 196 ticked rather than as empty panes. An empty Africa under a field
	// that says the whole world is covered reads as a bug, and it is also where
	// an edit starts - untick Brazil from Worldwide and you should land on 195,
	// not on 11 stray South Americans.
	const effective = value.worldwide ? EVERY_COUNTRY : value.countries

	const categories: TwoPaneMenuCategory[] = [
		{
			id: WORLDWIDE_ID,
			label: (
				<>
					Worldwide
					{/* The box beside it is `aria-hidden` (the DS hides the whole
					    leading-icon slot), and a category row has no way to carry
					    `aria-checked`, so the state is spelled out for a screen
					    reader here. */}
					<span className="sr-only">
						{value.worldwide ? ', selected' : ', not selected'}
					</span>
				</>
			),
			// No count to show, and the DS would otherwise draw its own.
			badge: null,
			// The DS's own inert-checkbox pattern (readOnly + tabIndex -1 +
			// aria-hidden + pointer-events-none), the one its multiple-mode rows
			// use, moved into the leading-icon slot: the row is a `<button>`, so a
			// live input inside it would be a second focus stop and would toggle
			// twice on one click. The row's own onClick is the control.
			// `sm` (16px) to match the country rows, which the stylesheet takes
			// down from the DS's 20 - see `region-picker.css`. The same control
			// has to be the same size across the two panes, so these two move
			// together or not at all. It also fits the 16px icon slot exactly.
			icon: (
				<Checkbox
					size="sm"
					checked={value.worldwide}
					readOnly
					tabIndex={-1}
					aria-hidden
					className="pointer-events-none"
				/>
			),
			// A category needs one or the other. Empty, because this row has no
			// pane: it is never the active category, so the pane never renders.
			items: []
		},
		...CONTINENTS.map((continent) => ({
			id: continent.id,
			label: continent.label,
			// The DS counts what it has ticked, and what it has ticked includes the
			// synthetic "All of …" row. Count the real countries instead.
			badge: (
				<CountBadge
					count={(effective[continent.id] || []).length}
					className={COUNT_BADGE_SIZE}
				/>
			),
			items: [
				{
					id: ALL_ID,
					label: `All of ${continent.label}`,
					searchText: `All of ${continent.label}`
				},
				...continent.countries.map((country) => ({
					id: country.id,
					label: country.label
				}))
			]
		}))
	]

	// What the DS draws ticked: the effective country ids, plus the synthetic
	// "All of …" row wherever a continent happens to be complete.
	const shown: Record<string, string[]> = {}
	CONTINENTS.forEach((continent) => {
		const ids = effective[continent.id] || []
		if (!ids.length) return
		shown[continent.id] = isFull(continent.id, ids) ? [ALL_ID, ...ids] : ids
	})

	return (
		<div className={TRIGGER_FIT}>
			<TwoPaneMenu
				trigger={
					<AppButton
						type="button"
						variant="secondary"
						size="md"
						roundness="default"
						label={<span className="min-w-0 truncate">{summarize(value)}</span>}
						rightIcon={<ChevronDown />}
						selected={open}
						disabled={disabled}
						aria-label={ariaLabel}
						aria-invalid={invalid || undefined}
						aria-describedby={invalid ? errorId : undefined}
					/>
				}
				categories={categories}
				selectedCategoryId={category}
				onSelectCategory={(id) => {
					// Worldwide is the one row that does not navigate: it toggles, and
					// the pane stays where it was. Unticking it drops the region to
					// nothing rather than snapping back, which is the whole point of
					// making it uncheckable - the caller flags the empty state.
					if (id === WORLDWIDE_ID) {
						onChange(value.worldwide ? NO_REGION : WORLDWIDE)
						return
					}
					setCategory(id)
				}}
				// Off for everyone, because it cannot be off for Worldwide alone: a
				// chevron on a row that opens nothing is a lie, and the setting is
				// all-or-nothing. The continents keep the count badge and the
				// selected tint, and the 16px it frees goes to the country names.
				categoryChevron={false}
				// Delegated from the panel, which portals to `document.body` -
				// see `titleWhenClipped`.
				onMouseOver={titleWhenClipped}
				// The handle `region-picker.css` shrinks the row checkboxes by. It
				// rides the same `{...rest}` spread as the handler above, so it
				// lands on the portaled panel where the rows actually live.
				data-region-panel=""
				selectionMode="multiple"
				selectedItemIds={shown}
				onSelectionChange={(next, changed) => {
					const continent = CONTINENTS.find(
						(entry) => entry.id === changed.categoryId
					)
					if (!continent) return

					const picked =
						changed.item.id === ALL_ID
							? changed.selected
								? continent.countries.map((country) => country.id)
								: []
							: (next[continent.id] || []).filter((id) => id !== ALL_ID)

					const countries = { ...effective, [continent.id]: picked }
					const total = Object.values(countries).reduce(
						(sum, ids) => sum + ids.length,
						0
					)
					// All 196 IS the whole world: storing it as a count would leave
					// Worldwide unticked on a panel where every country is ticked.
					// Zero used to fall back here too - it no longer does, so
					// unticking your last country leaves the region empty and the
					// caller can say so.
					onChange(
						total === TOTAL_COUNTRIES
							? WORLDWIDE
							: { worldwide: false, countries, unplaced: value.unplaced }
					)
				}}
				open={open && !disabled}
				onOpenChange={(next) => setOpen(next && !disabled)}
				searchPlaceholder="Search countries"
				// Match the prototype's inset search row and tinted country pane.
				searchVariant="bare"
				itemsPaneFill="canvas"
				panelWidth={compact ? COMPACT_PANEL_WIDTH : PANEL_WIDTH}
				// The default `fixed` strategy portals the panel into
				// `document.body` wearing the DS `cg-z-50`. The sheet is
				// `z-[9999]`, so inside the drawer the panel opens BEHIND it and
				// the field reads as dead. `scroll-parent` portals into the
				// drawer's own scroller instead, so the panel is above the sheet
				// and it scrolls natively with the body.
				anchorStrategy={compact ? 'scroll-parent' : undefined}
				paneHeight={PANE_HEIGHT}
			/>
		</div>
	)
}
